import React, { useState, useEffect } from 'react';
import '../styles/ImageCarousel.css';

const ImageCarousel = ({ images, alt, className }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageArray, setImageArray] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Parse the images prop into an array on component mount
  useEffect(() => {
    console.log('ImageCarousel: Raw images prop received:', images);
    
    if (!images) {
      setImageArray([]);
      return;
    }
    
    let result = [];
    
    // Handle different image formats
    if (typeof images === 'string') {
      // Check if it's a JSON string array (OBI format)
      if (images.startsWith('[') && images.endsWith(']')) {
        try {
          const parsed = JSON.parse(images);
          result = Array.isArray(parsed) ? parsed : [images];
        } catch (e) {
          // If parsing fails, treat as a single image
          result = [images];
        }
      } else if (images.includes(',')) {
        // Handle comma-separated URLs (Hagebau format)
        result = images.split(',').map(url => url.trim());
      } else {
        // Single image URL
        result = [images];
      }
    } else if (Array.isArray(images)) {
      // Already an array
      result = images;
    }
    
    console.log('ImageCarousel: Final image array:', result);
    setImageArray(result);
  }, [images]);
  
  // Reset current index when images change
  useEffect(() => {
    setCurrentIndex(0);
    setIsLoading(true);
  }, [imageArray]);
  
  // Reset loading state when current index changes
  useEffect(() => {
    setIsLoading(true);
  }, [currentIndex]);
  
  // Check if image is already loaded (from cache)
  useEffect(() => {
    if (imageArray.length > 0) {
      const img = new Image();
      img.src = imageArray[currentIndex] || '';
      
      // If image is already in cache, it might be complete already
      if (img.complete) {
        console.log('Image already in cache, setting loading to false');
        setIsLoading(false);
      }
    }
  }, [currentIndex, imageArray]);
  
  // Add a timeout to ensure loading state doesn't get stuck
  useEffect(() => {
    if (isLoading) {
      console.log('Setting up loading timeout fallback');
      const timer = setTimeout(() => {
        console.log('Loading timeout reached, forcing loading to false');
        setIsLoading(false);
      }, 3000); // 3 second fallback
      return () => clearTimeout(timer);
    }
  }, [isLoading]);
  
  // Image load handler
  const handleImageLoad = () => {
    console.log('Image loaded successfully');
    setIsLoading(false);
  };
  
  // Handle image load errors
  const handleImageError = (index) => {
    console.error(`ImageCarousel: Image at index ${index} failed to load:`, imageArray[index]);
    // We're not using a placeholder image anymore, so we don't need to track failed images
  };
  
  // Navigate to previous image
  const goToPrevious = () => {
    const isFirstImage = currentIndex === 0;
    const newIndex = isFirstImage ? imageArray.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
  };
  
  // Navigate to next image
  const goToNext = () => {
    const isLastImage = currentIndex === imageArray.length - 1;
    const newIndex = isLastImage ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
  };
  
  // If no images or only one image, render a simple img element
  if (imageArray.length <= 1) {
    const imageUrl = imageArray[0] || '';
    
    return (
      <div className="single-image-container">
        {isLoading && (
          <div className="image-loading-spinner">
            <div className="spinner"></div>
          </div>
        )}
        <img 
          src={imageUrl} 
          alt={alt || 'Product'} 
          className={`${className || 'product-image'} ${isLoading ? 'loading' : 'loaded'}`}
          onLoad={handleImageLoad}
          onError={() => {
            handleImageError(0);
            setIsLoading(false);
          }}
        />
      </div>
    );
  }
  
  // Get current image URL
  const currentImageUrl = imageArray[currentIndex] || '';
  
  return (
    <div className="image-carousel">
      <button 
        className="carousel-button prev-button" 
        onClick={goToPrevious}
        aria-label="Previous image"
      >
        &#10094;
      </button>
      
      <div className="carousel-image-container">
        {isLoading && (
          <div className="image-loading-spinner">
            <div className="spinner"></div>
          </div>
        )}
        <img 
          src={currentImageUrl} 
          alt={`${alt || 'Product'} - Image ${currentIndex + 1} of ${imageArray.length}`} 
          className={`${className || 'product-image'} ${isLoading ? 'loading' : 'loaded'}`}
          onLoad={handleImageLoad}
          onError={() => {
            handleImageError(currentIndex);
            setIsLoading(false);
          }}
        />
        
        <div className="image-counter">
          {currentIndex + 1}/{imageArray.length}
        </div>
      </div>
      
      <button 
        className="carousel-button next-button" 
        onClick={goToNext}
        aria-label="Next image"
      >
        &#10095;
      </button>
      
      <div className="carousel-dots">
        {imageArray.map((_, index) => (
          <span 
            key={index} 
            className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
            onClick={() => setCurrentIndex(index)}
            aria-label={`Go to image ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default ImageCarousel;
